import { isBoolean, range } from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import EventListener from "react-event-listener";
import { Draw } from "tone";
import classNames from "classnames";

import { Hit, MarchSubscriber } from "./core";

import "./StepSequencer.scss";

interface StepSequencerProps<C> {
  timeSteps: number;
  rows: readonly C[];
  rowHeaderImages: { [k: string]: string };
  currentHits: readonly Hit<C>[];
  currentAltHits?: readonly Hit<C>[];
  marchProvider: (sub: MarchSubscriber) => () => void;
  onToggleHit: (timeStep: number, row: C, isAlt: boolean) => void;
}

const _StepSequencer = <C extends string>({
  timeSteps,
  rows,
  rowHeaderImages,
  currentHits,
  currentAltHits,
  onToggleHit,
  marchProvider,
}: StepSequencerProps<C>) => {
  let [isAltMarching, setIsAltMarching] = useState(false);

  let cellRefs = useRef(new Map<C, Map<number, HTMLSpanElement>>());
  let updateCellRef = useCallback(
    (row: C, timeStep: number, el: HTMLSpanElement | null) => {
      let mp = cellRefs.current.get(row);
      if (!mp) {
        mp = new Map();
        cellRefs.current.set(row, mp);
      }
      if (el) {
        if (!mp) {
          mp = new Map();
          cellRefs.current.set(row, mp);
        }
        mp.set(timeStep, el);
      } else {
        mp.delete(timeStep);
      }
    },
    []
  );

  useEffect(() => {
    let unSub = marchProvider(
      (timeStep, previousTimestep, hits, triggerTime, isAltMarch) => {
        if (timeStep === -1) {
          Draw.schedule(() => {
            cellRefs.current.forEach((rowCells) => {
              rowCells.forEach((c) => c.classList.remove("isCurrent"));
            });
          }, triggerTime);
        } else {
          Draw.schedule(() => {
            cellRefs.current.forEach((rowCells) => {
              rowCells.get(previousTimestep)!.classList.remove("isCurrent");
              rowCells.get(timeStep)!.classList.add("isCurrent");
            });
          }, triggerTime);
        }
        isBoolean(isAltMarch) && setIsAltMarching(isAltMarch);
      }
    );
    return unSub;
  }, [marchProvider]);

  useEffect(() => {
    let hitmap =
      isAltMarching && currentAltHits
        ? makeHitMap(currentAltHits)
        : makeHitMap(currentHits);
    cellRefs.current.forEach((rowCells, row) => {
      rowCells.forEach((el, cellTimeStep) => {
        el.classList.toggle(
          "isChecked",
          hitmap.get(row)?.has(cellTimeStep) ?? false
        );
      });
    });
  }, [currentHits, currentAltHits, isAltMarching]);

  let pointerDown = useRef(false);
  let lastTouched = useRef<{ row: C; timeStep: number } | null>(null);

  let onPointerDown = useCallback(
    (row: C, timeStep: number, e: React.PointerEvent) => {
      pointerDown.current = true;
      onToggleHit(timeStep, row, isAltMarching);
      e.preventDefault();
      lastTouched.current = { row, timeStep };
    },
    [onToggleHit, isAltMarching]
  );
  let onPointerEnter = useCallback(
    (row: C, timeStep: number) => {
      if (pointerDown.current) {
        onToggleHit(timeStep, row, isAltMarching);
      }
    },
    [onToggleHit, isAltMarching]
  );
  let onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      let el = document.elementFromPoint(
        e.touches[0].clientX,
        e.touches[0].clientY
      ) as HTMLElement;
      while (el) {
        if (el.classList.contains("step")) {
          let row = el.dataset.n as C;
          let timeStep = parseInt(el.dataset.t as string, 10);
          if (
            lastTouched.current?.row !== row ||
            lastTouched.current?.timeStep !== timeStep
          ) {
            onToggleHit(timeStep, row, isAltMarching);
            lastTouched.current = { row, timeStep };
          }
          return;
        }
        el = el.parentElement as HTMLElement;
      }
    },
    [onToggleHit, isAltMarching]
  );

  return (
    <>
      <StepSequencerSteps
        timeSteps={timeSteps}
        rows={rows}
        rowHeaderImages={rowHeaderImages}
        updateCellRef={updateCellRef}
        onPointerDown={onPointerDown}
        onPointerEnter={onPointerEnter}
        onTouchMove={onTouchMove}
      />
      <EventListener
        target={document}
        onPointerUp={() => (pointerDown.current = false)}
      />
    </>
  );
};

interface StepSequencerStepsProps<C> {
  timeSteps: number;
  rows: readonly C[];
  rowHeaderImages: { [k: string]: string };
  updateCellRef: (
    row: C,
    timeStep: number,
    input: HTMLSpanElement | null
  ) => void;
  onPointerDown: (row: C, timeStep: number, e: React.PointerEvent) => void;
  onPointerEnter: (row: C, timeStep: number) => void;
  onTouchMove: (evt: React.TouchEvent) => void;
}
const _StepSequencerSteps = <C extends string>({
  timeSteps,
  rows,
  rowHeaderImages,
  updateCellRef,
  onPointerDown,
  onPointerEnter,
  onTouchMove,
}: StepSequencerStepsProps<C>) => (
  <div className="stepSequencer">
    {rows.map((n) => (
      <React.Fragment key={n}>
        <div className="rowLabel">
          <label>
            <img className="rowLabelImage" src={rowHeaderImages[n]} alt={n} />
          </label>
        </div>
        {range(timeSteps).map((t) => (
          <span
            className={classNames("step", n.replace("#", "s"))}
            key={t}
            ref={(el) => updateCellRef(n, t, el)}
            onPointerDown={(evt) => onPointerDown(n, t, evt)}
            onPointerEnter={(evt) => onPointerEnter(n, t)}
            onTouchMove={onTouchMove}
            data-t={t}
            data-n={n}
          >
            <svg
              className="stepCanvas"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 30 30"
            >
              <rect width="30" height="30" />
            </svg>
            <svg
              className="stepIcon stepIcon--square"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 30 30"
            >
              <rect className="large" width="30" height="30" />
              <rect className="small" x="13" y="13" width="4" height="4" />
            </svg>
            <svg
              className="stepIcon stepIcon--triangle"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 30 30"
            >
              <polygon className="large" points="0 30 15 0 30 30 0 30" />
              <polygon className="small" points="12 18 15 12 18 18 12 18" />
            </svg>
            <svg
              className="stepIcon stepIcon--circle"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 30 30"
            >
              <circle className="large" cx="15" cy="15" r="15" />
              <circle className="small" cx="15" cy="15" r="2.5" />
            </svg>
          </span>
        ))}
      </React.Fragment>
    ))}
  </div>
);
export const StepSequencerSteps = React.memo(
  _StepSequencerSteps
) as typeof _StepSequencerSteps;

export const StepSequencer = React.memo(
  _StepSequencer
) as typeof _StepSequencer;

function makeHitMap<C>(hits: readonly Hit<C>[]): Map<C, Set<number>> {
  let res = new Map<C, Set<number>>();
  for (let hit of hits) {
    if (!res.has(hit.note)) {
      res.set(hit.note, new Set());
    }
    res.get(hit.note)!.add(hit.timeStep);
  }
  return res;
}
